import { useEffect, useMemo, useState } from "react";
import DSAutocomplete from "views/system-design/DSAutocomplete";
import DSBox from "views/system-design/DSBox";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSDialog from "views/system-design/DSDialog";
import DSDialogContent from "views/system-design/DSDialogContent";
import DSDialogTitle from "views/system-design/DSDialogTitle";
import DSTextField from "views/system-design/DSTextField";
import DSTypography from "views/system-design/DSTypography";
import { ClocksListResponse } from "../../../Types";
import { ClockConfigValue } from "../../../api/clockConfig";
import { extractApiErrorMessage } from "../../../utils/util";
import useClocksInfiniteQuery from "../../Device/hooks/useClocksInfiniteQuery";
import useListboxInfiniteScroll, {
  noClientFilter,
} from "../../Device/hooks/useListboxInfiniteScroll";
import {
  useClockGlobalConfig,
  useUpdateClockConfigOverride,
} from "../hooks/useClockConfig";
import ClockConfigForm from "./ClockConfigForm";

export interface ClockConfigOverrideDialogProps {
  open: boolean;
  onClose: () => void;
  /** The clock being edited. Omit to let the admin search for one (the "New override" path). */
  clockId?: string;
  /** Values already stored on that clock's override, if any. */
  values?: Record<string, ClockConfigValue>;
  /** Clocks that already have an override, so the search does not offer them twice. */
  excludeClockIds?: string[];
}

/**
 * Edits one clock's configuration override.
 *
 * Only keys the global document already sets can be overridden: a clock deviating from a
 * value the fleet does not have would be a value nobody can find again. Set it globally
 * first, then override it here.
 *
 * Keys left out fall back to the global document — which is why the global values are
 * loaded here and shown as each row's fallback.
 */
const ClockConfigOverrideDialog = ({
  open,
  onClose,
  clockId,
  values,
  excludeClockIds = [],
}: ClockConfigOverrideDialogProps) => {
  const [selectedClock, setSelectedClock] = useState<ClocksListResponse | null>(
    null,
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: globalConfig, isLoading: isLoadingGlobal } =
    useClockGlobalConfig();
  const { mutate: save, isPending: isSaving } = useUpdateClockConfigOverride();

  // Only needed on the "New override" path; the device modal already knows its clock.
  const {
    data: clocks,
    isLoading: isLoadingClocks,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useClocksInfiniteQuery({
    query: searchQuery,
    registrationStatus: "ALL",
  });

  const { listboxProps } = useListboxInfiniteScroll({
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  });

  const clockOptions = useMemo(() => {
    if (!clocks) return [];
    const excluded = new Set(excludeClockIds);
    return clocks.filter((clock) => !excluded.has(clock.id));
  }, [clocks, excludeClockIds]);

  useEffect(() => {
    if (open) {
      setSelectedClock(null);
      setSearchQuery("");
      setError(null);
    }
  }, [open, clockId]);

  const globalValues = globalConfig?.values ?? {};
  const globalKeys = useMemo(
    () => Object.keys(globalValues).sort(),
    // eslint-disable-next-line
    [JSON.stringify(globalValues)],
  );

  const targetClockId = clockId ?? selectedClock?.id;

  const handleSave = (next: Record<string, ClockConfigValue>) => {
    if (!targetClockId) {
      setError("Pick a clock first");
      return;
    }
    setError(null);
    save(
      { clockId: targetClockId, values: next },
      {
        onSuccess: () => onClose(),
        onError: (e) =>
          setError(extractApiErrorMessage(e, "Failed to save the override")),
      },
    );
  };

  return (
    <DSDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>
        {clockId ? `Config override — ${clockId}` : "New config override"}
      </DSDialogTitle>
      <DSDialogContent>
        <DSBox sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {!clockId && (
            <DSAutocomplete<ClocksListResponse>
              options={clockOptions}
              filterOptions={noClientFilter}
              getOptionLabel={(option) => `${option.idf} (${option.id})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedClock}
              onChange={(_, value) => setSelectedClock(value)}
              inputValue={searchQuery}
              onInputChange={(_, value) => setSearchQuery(value)}
              loading={isLoadingClocks || isFetchingNextPage}
              ListboxProps={listboxProps}
              disabled={isSaving}
              renderInput={(params) => (
                <DSTextField
                  {...params}
                  label="Search by serial, friendly ID..."
                  size="small"
                  autoFocus
                />
              )}
            />
          )}

          <DSTypography variant="caption" color="text.secondary">
            Only keys the global configuration already sets can be overridden.
            Everything else follows the global document. The clock picks a
            change up at its daily 04:xx fetch — send the &quot;Refresh
            Config&quot; command from the device commands to apply it now.
          </DSTypography>

          {isLoadingGlobal ? (
            <DSBox sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <DSCircularProgress />
            </DSBox>
          ) : (
            <ClockConfigForm
              values={values ?? {}}
              inheritedValues={globalValues}
              inheritLabel="Global"
              allowedKeys={globalKeys}
              saving={isSaving}
              error={error}
              saveLabel="Save override"
              onSave={handleSave}
              onCancel={onClose}
            />
          )}
        </DSBox>
      </DSDialogContent>
    </DSDialog>
  );
};

export default ClockConfigOverrideDialog;
